import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@menlo/ui';
import { queryClient } from './lib/query-client';
import { ProtectedRoute } from './components/protected-route';
import { ScrollToTop } from './components/scroll-to-top';
import ErrorBoundary from './components/error-boundary';
import { FullScreenLoader } from './components/full-screen-loader';
import { WhatsappButton } from './components/whatsapp-button';
import { LEGACY_ROUTE_PATTERNS, rewriteLegacyPath, routes } from './routes';
import './App.css';

const LoginPage = lazy(() => import('./pages/login').then((m) => ({ default: m.LoginPage })));
const InvoicePage = lazy(() => import('./features/invoices/invoice').then((m) => ({ default: m.InvoicePage })));
const InvoiceListPage = lazy(() =>
  import('./features/invoices/invoice-list').then((m) => ({ default: m.InvoiceListPage })),
);
const NegotiationPage = lazy(() =>
  import('./features/negotiations/negotiation').then((m) => ({ default: m.NegotiationPage })),
);
const NegotiationListPage = lazy(() =>
  import('./features/negotiations/negotiation-list').then((m) => ({ default: m.NegotiationListPage })),
);

// Links já compartilhados em inglês (WhatsApp, e-mail) caem aqui e são redirecionados
// pra rota canônica em pt-BR preservando :id, query e hash.
function LegacyRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={rewriteLegacyPath(pathname) + search + hash} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<FullScreenLoader />}>
            <Routes>
              <Route path={routes.login} element={<LoginPage />} />
              {/* Detail routes stay outside ProtectedRoute so links (WhatsApp, email)
                  land the user on the page first; apiFetch bounces to /login on 401. */}
              <Route path={routes.invoiceDetailPath} element={<InvoicePage />} />
              <Route path={routes.negotiationDetailPath} element={<NegotiationPage />} />
              <Route element={<ProtectedRoute />}>
                <Route path={routes.invoiceList} element={<InvoiceListPage />} />
                <Route path={routes.negotiationList} element={<NegotiationListPage />} />
              </Route>
              {LEGACY_ROUTE_PATTERNS.map((path) => (
                <Route key={path} path={path} element={<LegacyRedirect />} />
              ))}
              <Route path="*" element={<Navigate to={routes.invoiceList} replace />} />
            </Routes>
          </Suspense>
          <Toaster />
          <WhatsappButton />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
